import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import {
  getInventoryAnalysis,
  getSetTargetLeadData,
  updateTargets,
  updateManagementPriorities,
  getAllSalesArsByProjectId
} from '../../services/api/implementation/impl';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import AiSectionBanner from '../../components/ai/AiSectionBanner';
import toast from 'react-hot-toast';
import { numberToWords } from './../../utils/numberToWords';
import { useSmartBack } from '../../../shared/navigation';

const SetTarget = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/management' });
  const userID = Number(Cookies.get('userId'));

  const [filters, setFilters] = useState({
    projectID: location.state?.projectID || null,
    month: location.state?.month || null
  });
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [selectedLeadType, setSelectedLeadType] = useState(null); // 'hot' or 'warm'
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [targetValue, setTargetValue] = useState('');
  const priorityId = location.state?.priorityId || null;

  // Fetch lead data
  const {
    data: leadData,
    isLoading: isLoadingLeadData,
    refetch: refetchLeadData
  } = useQuery({
    queryKey: ['setTargetLeadData', filters.projectID, filters.month],
    queryFn: () => getSetTargetLeadData(filters.projectID, filters.month),
    enabled: !!filters.projectID && !!filters.month
  });

  // Fetch inventory analysis
  const {
    data: inventoryData,
    isLoading: isLoadingInventory,
    refetch: refetchInventory
  } = useQuery({
    queryKey: ['inventoryAnalysis', filters.projectID, filters.month],
    queryFn: () => getInventoryAnalysis(filters.projectID, filters.month),
    enabled: !!filters.projectID && !!filters.month
  });

  // Fetch AR data
  const { data: allARs, isLoading: isLoadingARs } = useQuery({
    queryKey: ['Ars', filters.projectID, filters.month],
    queryFn: () => getAllSalesArsByProjectId(filters.projectID, filters.month),
    enabled: !!filters.projectID && !!filters.month
  });

  // Calculate metrics from lead data
  const calculateMetrics = () => {
    if (!leadData?.data?.sales) return null;

    const sales = leadData.data.sales;
    let hotCount = 0;
    let warmCount = 0;
    let coldCount = 0;
    const hotLeads = [];
    const warmLeads = [];
    const executiveHotLeads = {};
    const executiveWarmLeads = {};

    sales.forEach((executive) => {
      if (executive.leads && executive.leads.length > 0) {
        executive.leads.forEach((lead) => {
          if (lead.pstatus === 'Hot') {
            hotCount++;
            hotLeads.push(lead);
            if (!executiveHotLeads[executive.id]) {
              executiveHotLeads[executive.id] = {
                executive: executive,
                leads: []
              };
            }
            executiveHotLeads[executive.id].leads.push(lead);
          } else if (lead.pstatus === 'Warm') {
            warmCount++;
            warmLeads.push(lead);
            if (!executiveWarmLeads[executive.id]) {
              executiveWarmLeads[executive.id] = {
                executive: executive,
                leads: []
              };
            }
            executiveWarmLeads[executive.id].leads.push(lead);
          } else {
            coldCount++;
          }
        });
      }
    });

    return {
      hotCount,
      warmCount,
      coldCount,
      hotLeads,
      warmLeads,
      executiveHotLeads,
      executiveWarmLeads,
      hotLeadsPotentialAmount: leadData?.data?.hotLeadsPotentialAmount || 0,
      warmLeadBookingAmount: leadData?.data?.warmLeadBookingAmount || 0
    };
  };

  const metrics = calculateMetrics();
  const projectedTarget = metrics ? metrics.hotLeadsPotentialAmount * 0.3 : 0;

  const handleHotClick = () => {
    setSelectedLeadType('hot');
    setShowLeadsModal(true);
  };

  const handleWarmClick = () => {
    setSelectedLeadType('warm');
    setShowLeadsModal(true);
  };

  const handleExecutiveClick = (executiveId, type) => {
    setSelectedExecutive(executiveId);
    setSelectedLeadType(type);
    setShowLeadsModal(true);
  };

  const getLeadsForModal = () => {
    if (!metrics) return [];
    if (selectedExecutive) {
      if (selectedLeadType === 'hot') {
        return metrics.executiveHotLeads[selectedExecutive]?.leads || [];
      } else {
        return metrics.executiveWarmLeads[selectedExecutive]?.leads || [];
      }
    } else {
      if (selectedLeadType === 'hot') {
        return metrics.hotLeads;
      } else {
        return metrics.warmLeads;
      }
    }
  };

  // Mutation for setting target
  const setTargetMutation = useMutation({
    mutationFn: (payload) => updateTargets(payload),
    onSuccess: () => {
      toast.success('Target set successfully');
      // After setting target, mark priority as complete
      if (priorityId) {
        updatePriorityMutation.mutate({
          id: priorityId,
          payload: { status: 'Completed' }
        });
      } else {
        // If no priorityId, just navigate back
        navigate('/builder/dashboard');
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to set target');
    }
  });

  // Mutation for updating priority
  const updatePriorityMutation = useMutation({
    mutationFn: ({ id, payload }) => updateManagementPriorities(id, payload),
    onSuccess: () => {
      toast.success('Priority marked as complete');
      navigate('/builder/dashboard');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update priority');
    }
  });

  // Handle set target button click
  const handleSetTarget = () => {
    if (!targetValue || isNaN(Number(targetValue)) || Number(targetValue) <= 0) {
      toast.error('Please enter a valid target value');
      return;
    }

    if (!filters.projectID || !filters.month) {
      toast.error('Project and month are required');
      return;
    }

    // Get current year and format month_date
    const currentYear = new Date().getFullYear();
    const monthPadded = String(filters.month).padStart(2, '0');
    const monthDateIso = `${currentYear}-${monthPadded}-01T00:00:00.000Z`;

    // Build payload - set target only for the sales manager
    const salesManager = inventoryData?.data?.salesManager;

    if (!salesManager || !salesManager.id) {
      toast.error('Sales manager not found for this project');
      return;
    }

    // Get AR target for the sales manager (similar to TargetsAndIncentives.jsx)
    const matchedAR = allARs?.data?.find((ar) => ar.id === Number(salesManager.id));
    const arValue = matchedAR ? matchedAR.ar : 0;

    // Clean AR target value (similar to TargetsAndIncentives.jsx)
    const cleanedArTarget =
      typeof arValue === 'string' ? Number(arValue.replace(/[^0-9.-]+/g, '')) : Number(arValue);

    const payload = [
      {
        sales_user_id: Number(salesManager.id),
        target_amount: Number(targetValue),
        incentive: 0, // Default incentive value
        ar_target: cleanedArTarget,
        project_id: Number(filters.projectID),
        month_date: monthDateIso
      }
    ];

    setTargetMutation.mutate(payload);
  };

  return (
    <Layout>
      <Loader
        isVisible={
          isLoadingLeadData ||
          isLoadingInventory ||
          isLoadingARs ||
          setTargetMutation.isPending ||
          updatePriorityMutation.isPending
        }
      />

      <div
        className={`fixed top-0 z-10 transition-all duration-300 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header data={{ name: 'Set Target' }} showProjectSelector={false} backArrow={true} onBack={onBack} />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="pt-[6rem] p-6">
          <div className="mb-4 max-w-4xl">
            <AiSectionBanner projectId={filters.projectID} vertical="management" surface="set_target" />
          </div>
          {!filters.projectID || !filters.month ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                Please select a project and month to view target data.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* New Bookings Target Section */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">New Bookings Target</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div
                    className="border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50"
                    onClick={handleHotClick}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">Hot Leads</span>
                      <span className="text-2xl font-bold text-red-600">
                        {metrics?.hotCount || 0}
                      </span>
                    </div>
                  </div>
                  <div
                    className="border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50"
                    onClick={handleWarmClick}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">Warm Leads</span>
                      <span className="text-2xl font-bold text-orange-600">
                        {metrics?.warmCount || 0}
                      </span>
                    </div>
                  </div>
                  <div className="border-2 border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">Cold Leads</span>
                      <span className="text-2xl font-bold text-gray-600">
                        {metrics?.coldCount || 0}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Potential Booking Amounts */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">
                      Potential Booking Amount (Hot Leads)
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      ₹{metrics?.hotLeadsPotentialAmount?.toLocaleString('en-IN') || '0'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Range estimate</p>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">
                      Potential Booking Amount (Warm Leads)
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      ₹{metrics?.warmLeadBookingAmount?.toLocaleString('en-IN') || '0'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Avg unit price per lead (Next 40 days)
                    </p>
                  </div>
                </div>
              </div>

              {/* Projected Target Section */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Projected Target</h2>
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">
                    30% of Estimated Amount from Hot Leads
                  </p>
                  <p className="text-2xl font-bold text-primary">
                    ₹{projectedTarget.toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              {/* Inventory Aging Section */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Inventory Aging</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">90+ Days</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {inventoryData?.data?.ninetyDaysUnit || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">units not sold</p>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">180+ Days</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {inventoryData?.data?.oneEightyDaysUnit || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">units not sold</p>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">360+ Days</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {inventoryData?.data?.threeSixtyDaysUnit || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">units not sold</p>
                  </div>
                </div>
              </div>

              {/* Set Target Section */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Set New Bookings Target</h2>
                <div className="flex items-start gap-4">
                  <div className="flex-1 max-w-md">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Bookings Target Value
                    </label>
                    <input
                      type="number"
                      value={targetValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive numbers
                        if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
                          setTargetValue(value);
                        }
                      }}
                      placeholder="Enter target value"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {targetValue && (
                      <span className="text-sm text-gray-600 mt-1 italic">
                        {numberToWords(Number(targetValue))}
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={handleSetTarget}
                    customStyle={{ marginTop: '1.7rem' }}
                    disabled={
                      !targetValue ||
                      !inventoryData?.data?.salesManager ||
                      setTargetMutation.isPending ||
                      updatePriorityMutation.isPending
                    }>
                    {setTargetMutation.isPending || updatePriorityMutation.isPending
                      ? 'Setting...'
                      : 'Set'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Leads Modal */}
      {showLeadsModal && (
        <Modal
          name={`${selectedLeadType === 'hot' ? 'Hot' : 'Warm'} Leads${selectedExecutive ? ' - Executive Details' : ''}`}
          width={80}
          onClose={() => {
            setShowLeadsModal(false);
            setSelectedLeadType(null);
            setSelectedExecutive(null);
          }}>
          <div className="max-h-[70vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">
                    Lead Name
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">
                    Phone
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">
                    Email
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">
                    Status
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">
                    Interested Project Accommodations
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">
                    Executive
                  </th>
                </tr>
              </thead>
              <tbody>
                {getLeadsForModal().map((lead) => {
                  const executive = leadData?.data?.sales?.find((s) => s.id === lead.sales_id);
                  return (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 text-sm">
                        {lead.user?.name || 'N/A'}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">
                        {lead.user?.phone_number || lead.alternate_phone_number || 'N/A'}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">
                        {lead.user?.email || 'N/A'}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            lead.pstatus === 'Hot'
                              ? 'bg-red-100 text-red-800'
                              : lead.pstatus === 'Warm'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                          {lead.pstatus || 'Cold'}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">
                        {lead.accomodation?.map((type) => type.name).join(', ') || 'N/A'}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">
                        {executive?.name || 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {getLeadsForModal().length === 0 && (
              <div className="text-center py-8 text-gray-500">No leads found</div>
            )}
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default SetTarget;
